import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://e-matrix.at';
const SITE_NAME = 'e-Matrix Innovations';
const DEFAULT_IMAGE = `${SITE_URL}/og-default.jpg`;

// Default keywords for the site
const DEFAULT_KEYWORDS_DE = 'E-Learning Österreich, Arbeitssicherheit E-Learning, Unterweisungssystem, elektronische Unterweisung, Besucherunterweisung, Cyber Security Awareness, DSGVO Schulung, Compliance Training, SCORM, LMS, Corporate Training';
const DEFAULT_KEYWORDS_EN = 'E-Learning Austria, Workplace Safety E-Learning, Training System, Electronic Instruction, Visitor Management, Cyber Security Awareness, GDPR Training, Compliance Training, SCORM, LMS, Corporate Training';

/**
 * Build a language-prefixed URL. `path` must start with '/'. Empty / root path
 * yields '/{lang}'.
 */
function localizedUrl(lang, path) {
  const normalized = (path && path !== '/') ? path : '';
  return `${SITE_URL}/${lang}${normalized}`;
}

export function SEO({
  title,
  description,
  keywords,
  path = '/',
  image,
  type = 'website',
  lang = 'de',
  noindex = false,
  jsonLd = null,
  altPaths = null   // optional { de: '/datenschutz', en: '/privacy-policy' }
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Corporate E-Learning`;
  const canonicalUrl = localizedUrl(lang, path);
  const ogImage = image || DEFAULT_IMAGE;
  const metaKeywords = keywords || (lang === 'de' ? DEFAULT_KEYWORDS_DE : DEFAULT_KEYWORDS_EN);

  // hreflang alternates for the same page in the other language
  const dePath = (altPaths && altPaths.de) || path;
  const enPath = (altPaths && altPaths.en) || path;
  const deUrl = localizedUrl('de', dePath);
  const enUrl = localizedUrl('en', enPath);

  return (
    <Helmet prioritizeSeoTags>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={metaKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* hreflang alternates — tells Google both language versions exist */}
      <link rel="alternate" hrefLang="de" href={deUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={deUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={lang === 'de' ? 'de_AT' : 'en_US'} />
      <meta property="og:locale:alternate" content={lang === 'de' ? 'en_US' : 'de_AT'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured data (JSON-LD) for rich results */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
